import { useState } from 'react'
import { StatusBadge } from '@/components/ui/status-badge'
import { useUpdateBuilderApplication } from '@/hooks/use-builder-applications'
import type { BuilderApplication } from '@/lib/types'

interface ApplicationDetailPanelProps {
  application: BuilderApplication
  onClose: () => void
}

export function ApplicationDetailPanel({
  application,
  onClose,
}: ApplicationDetailPanelProps): React.JSX.Element {
  const [notes, setNotes] = useState(application.admin_notes ?? '')
  const updateMutation = useUpdateBuilderApplication()

  function handleAction(status: 'approved' | 'denied'): void {
    const label = status === 'approved' ? 'approve' : 'deny'
    if (!window.confirm(`Are you sure you want to ${label} this application?`)) return
    updateMutation.mutate(
      { id: application.id, status, admin_notes: notes || undefined },
      { onSuccess: onClose },
    )
  }

  function handleSaveNotes(): void {
    updateMutation.mutate({ id: application.id, admin_notes: notes || undefined })
  }

  return (
    <tr>
      <td colSpan={7} className="bg-surface-1 px-4 py-4">
        <div className="space-y-4 rounded-lg border border-surface-3 bg-surface-0 p-5">
          {/* Header */}
          <div className="flex items-center justify-between">
            <h3 className="text-base font-semibold text-text-primary">Application Details</h3>
            <button
              type="button"
              onClick={onClose}
              className="text-sm text-text-tertiary hover:text-text-primary"
            >
              Close
            </button>
          </div>

          {/* Fields grid */}
          <div className="grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2 lg:grid-cols-3">
            <DetailField label="Name" value={application.name} />
            <DetailField label="Email" value={application.email} />
            <DetailField label="Type">
              <StatusBadge status={application.type} />
            </DetailField>
            <DetailField label="Company / School" value={application.company_or_school} />
            <DetailField label="Location" value={application.location} />
            <DetailField label="Website" value={application.website} />
            <DetailField label="GitHub" value={application.github} />
            <DetailField label="Expected Volume" value={application.expected_volume} />
            <DetailField label="Status">
              <StatusBadge status={application.status} />
            </DetailField>
          </div>

          {/* Description */}
          <DetailBlock label="Description" value={application.description} />

          {/* Use Case */}
          {application.use_case && <DetailBlock label="Use Case" value={application.use_case} />}

          {/* Review info */}
          {application.reviewed_by && (
            <div className="rounded-lg bg-surface-1 px-4 py-3 text-sm text-text-secondary">
              Reviewed by{' '}
              <span className="font-medium text-text-primary">{application.reviewed_by}</span>
              {application.reviewed_at && (
                <> on {new Date(application.reviewed_at).toLocaleString()}</>
              )}
            </div>
          )}

          {/* Admin notes */}
          <div>
            <label
              htmlFor="app-admin-notes"
              className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-text-secondary"
            >
              Admin Notes
            </label>
            <textarea
              id="app-admin-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="w-full rounded-lg border border-surface-3 bg-surface-0 px-3 py-2 text-sm text-text-primary placeholder:text-text-tertiary focus:border-admin-500 focus:outline-none focus:ring-1 focus:ring-admin-500"
              placeholder="Add notes about this application..."
            />
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3">
            {application.status === 'pending' && (
              <>
                <button
                  type="button"
                  onClick={() => handleAction('approved')}
                  disabled={updateMutation.isPending}
                  className="rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
                >
                  {updateMutation.isPending ? 'Saving...' : 'Approve'}
                </button>
                <button
                  type="button"
                  onClick={() => handleAction('denied')}
                  disabled={updateMutation.isPending}
                  className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
                >
                  {updateMutation.isPending ? 'Saving...' : 'Deny'}
                </button>
              </>
            )}
            <button
              type="button"
              onClick={handleSaveNotes}
              disabled={updateMutation.isPending}
              className="rounded-lg border border-surface-3 bg-surface-0 px-4 py-2 text-sm font-medium text-text-primary hover:bg-surface-1 disabled:opacity-50"
            >
              Save Notes
            </button>
            {updateMutation.isError && (
              <span className="text-sm text-red-600">Failed to update. Please try again.</span>
            )}
          </div>
        </div>
      </td>
    </tr>
  )
}

function DetailField({
  label,
  value,
  children,
}: {
  label: string
  value?: string | null
  children?: React.ReactNode
}): React.JSX.Element {
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wider text-text-secondary">{label}</dt>
      <dd className="mt-0.5 text-sm text-text-primary">
        {children ?? value ?? <span className="text-text-tertiary">-</span>}
      </dd>
    </div>
  )
}

function DetailBlock({ label, value }: { label: string; value: string | null }): React.JSX.Element {
  return (
    <div>
      <dt className="mb-1 text-xs font-medium uppercase tracking-wider text-text-secondary">
        {label}
      </dt>
      <dd className="rounded-lg bg-surface-1 px-4 py-3 text-sm text-text-primary whitespace-pre-wrap">
        {value ?? <span className="text-text-tertiary">-</span>}
      </dd>
    </div>
  )
}
